import { useCallback, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  validateImageFile,
  validateUploadFileSize,
  getErrorMessage,
  getTooLargeMessage,
  formatFileSize,
} from "@/hooks/uploads/uploadValidation";
import { uploadFileToStorage } from "@/lib/uploadFileToStorage";
import { ContentExtractionService } from "@/services/knowledgebase/ContentExtractionService";
import { useAuth } from "@/auth/AuthProvider";
import { processingDocumentsStorage } from "@/lib/processingDocumentsStorage";
import AgentSelection from "../AgentSelection";
import { UploadComponentProps } from "./types";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface UploadImageProps extends UploadComponentProps {
  imageFiles: File[];
  onImageFilesChange: (files: File[]) => void;
}

const UploadImage = ({
  availableAgents,
  selectedAgentId,
  onAgentSelect,
  imageFiles,
  onImageFilesChange,
  onUploadSuccess,
  onDeferredActionAdd,
}: UploadImageProps) => {
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const { user } = useAuth();
  const queryClient = useQueryClient();

  const uploadImageFiles = useCallback(
    async (files: File[]) => {
      try {
        setIsUploadingImage(true);
        const uploadToast = toast.loading(`Uploading ${files.length} image(s)...`);

        const fileUrls: string[] = [];
        const fileNames: string[] = [];

        for (const file of files) {
          const { fileUrl } = await uploadFileToStorage(file, "temp-uploads");
          fileUrls.push(fileUrl);
          fileNames.push(file.name);
        }

        toast.dismiss(uploadToast);

        if (onDeferredActionAdd) {
          onDeferredActionAdd({
            type: "image-batch",
            data: {
              fileUrls,
              fileNames,
            },
          });
          toast.success(`${files.length} image(s) uploaded and queued for text extraction`);
        } else {
          const data = await ContentExtractionService.uploadImageFiles(fileUrls, fileNames, user!.id, selectedAgentId);

          if (data?.document?.id) {
            processingDocumentsStorage.add({
              documentId: data.document.id,
              agentId: selectedAgentId,
              startedAt: Date.now(),
            });
            window.dispatchEvent(new Event("processing-documents-changed"));
          }

          queryClient.invalidateQueries({ queryKey: ['documents'] });
          queryClient.invalidateQueries({ queryKey: ['documents-infinite'] });
          queryClient.invalidateQueries({ queryKey: ['knowledge-base-docs'] });

          toast.info(`${files.length} image(s) uploaded. Text extraction in progress - check back soon.`);
          onUploadSuccess?.();
        }

        onImageFilesChange([]);
      } catch (error) {
        toast.error(`Failed to upload images: ${(error as Error).message}`);
        console.error("Upload error:", error);
      } finally {
        setIsUploadingImage(false);
      }
    },
    [onDeferredActionAdd, onImageFilesChange, user, selectedAgentId, queryClient, onUploadSuccess],
  );

  const handleImageFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      const validFiles: File[] = [];
      for (const file of files) {
        if (!validateImageFile(file)) {
          toast.error(`${file.name}: ${getErrorMessage("image")}`);
        } else if (!validateUploadFileSize(file)) {
          toast.error(getTooLargeMessage(file));
        } else {
          validFiles.push(file);
        }
      }

      if (validFiles.length === 0) return;

      if (onDeferredActionAdd) {
        await uploadImageFiles(validFiles);
        e.target.value = "";
      } else {
        onImageFilesChange(validFiles);
      }
    },
    [onImageFilesChange, onDeferredActionAdd, uploadImageFiles],
  );

  const handleImageDrop = useCallback(
    async (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length === 0) return;

      const validFiles: File[] = [];
      for (const file of files) {
        if (!validateImageFile(file)) {
          toast.error(`${file.name}: ${getErrorMessage("image")}`);
        } else if (!validateUploadFileSize(file)) {
          toast.error(getTooLargeMessage(file));
        } else {
          validFiles.push(file);
        }
      }

      if (validFiles.length === 0) return;

      if (onDeferredActionAdd) {
        await uploadImageFiles(validFiles);
      } else {
        onImageFilesChange(validFiles);
      }
    },
    [onImageFilesChange, onDeferredActionAdd, uploadImageFiles],
  );

  const handleImageDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
  };

  return (
    <>
      <AgentSelection
        agents={availableAgents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={onAgentSelect}
      />
      <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto">
        <GenieIcon name="palette" size={64} color="#4E9CFF" gradientEnabled className="mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Extract Image Text</h3>
        <p className="text-sm text-gray-600 mb-6 text-center">Upload images (JPEG, PNG, GIF, WebP) to extract text</p>

        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleImageFileChange}
          multiple
          className="hidden"
          id="image-file-input"
        />
        <label
          htmlFor="image-file-input"
          onDrop={handleImageDrop}
          onDragOver={handleImageDragOver}
          className="flex items-center justify-center w-full max-w-md h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-genie-primary transition-colors bg-gray-50"
        >
          {imageFiles.length > 0 ? (
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">{imageFiles.length} image(s) selected</p>
              <p className="text-xs text-gray-500 mt-1">
                {imageFiles.reduce((sum, f) => sum + f.size, 0) > 0 && formatFileSize(imageFiles.reduce((sum, f) => sum + f.size, 0))}
              </p>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm text-gray-600">Click or drag to upload images</p>
              <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF, or WebP (multiple files supported)</p>
            </div>
          )}
        </label>
        {imageFiles.length > 0 && (
          <Button
            onClick={() => {
              uploadImageFiles(imageFiles);
            }}
            disabled={imageFiles.length === 0 || isUploadingImage}
            className="mt-4 bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold px-8 h-12 shadow-lg hover:shadow-xl transition-all hover:scale-105 rounded-xl"
          >
            {isUploadingImage ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              "Upload Images"
            )}
          </Button>
        )}
      </div>
    </>
  );
};

export default UploadImage;
